import { Close } from "@mui/icons-material";
import {
  alpha,
  Avatar,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  lighten,
  Menu,
  MenuItem,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { useNotificationRedirection } from "../hooks/useNotificationRedirection";
import { AppNotification } from "../hooks/useNotifications";
import { useEffect, useRef } from "react";
import { apiClient } from "../../../lib";

interface NotificationMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  notifications: AppNotification[];
  loadNotifications: (reset?: boolean) => void;
  hasMore: boolean;
  isLoading: boolean;
}

export const NotificationMenu = ({
  anchorEl,
  open,
  onClose,
  notifications,
  loadNotifications,
  hasMore,
  isLoading,
}: NotificationMenuProps) => {
  const theme = useTheme();

  const { handleNotificationClick } = useNotificationRedirection();

  const getTimeAgo = (date: string): string => {
    const now = dayjs();
    const notificationTime = dayjs.utc(date).local();
    const diffSeconds = now.diff(notificationTime, "second");
    if (diffSeconds < 60) return `${diffSeconds} secs ago`;
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)} mins ago`;
    if (diffSeconds < 86400) {
      const hours = Math.floor(diffSeconds / 3600);
      const minutes = Math.floor((diffSeconds % 3600) / 60);
      return `${hours} hrs ${minutes} mins ago`;
    }
    const days = Math.floor(diffSeconds / 86400);
    return `${days} days ago`;
  };

  const markNotificationRead = async (id: number) => {
    try {
      await apiClient.put(`/notification/read/${id}`);
    } catch (err) {
      console.error("Failed to mark notification as read", err);
    }
  };

  const notificationClick = async (notification: AppNotification) => {
    try {
      if (!notification.read) {
        await markNotificationRead(notification.id);
      }
      handleNotificationClick(notification);
    } catch (err) {
      console.error(err);
    } finally {
      onClose();
    }
  };

  const scrollTimeout = useRef<number | null>(null);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;

    if(scrollTimeout.current) clearTimeout(scrollTimeout.current);
    scrollTimeout.current = window.setTimeout(() => {
      if(!target) return;

      const threshold = 50;
      const atBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight <
        threshold;

      if (atBottom && hasMore && !isLoading) {
        loadNotifications();
      }
    }, 150);
  };

  useEffect(() => {
    if (open) {
      loadNotifications(true);
    }
  }, [open]);

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            maxWidth: 450,
            width: "100%",
          },
        },
        list: {
          disablePadding: true,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignContent: "center",
          justifyContent: "space-between",
          padding: 1,
          borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
          bgcolor: "action.hover",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            padding: 1,
          }}
        >
          Notifications
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </Box>

      <Divider />

      <Box
        sx={{
          maxHeight: 400,
          overflowY: "auto",
          scrollBehavior: "smooth"
        }}
        onScroll={(e) => handleScroll(e)}
      >
        {notifications.length === 0 && isLoading ? (
          <Box sx={{ p: 2 }}>
            <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={60} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 400,
            }}
          >
            <Typography>No Notifications Available</Typography>
          </Box>
        ) : (
          notifications?.map((notification) => (
            <MenuItem
              key={notification.id}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                padding: 2,
                gap: 2,
                mb: 1,
                position: "relative",
                borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                ":last-child": {
                  borderBottom: "none",
                },
                bgcolor: notification.read
                  ? "transparent"
                  : theme.palette.mode === "dark"
                    ? alpha(theme.palette.primary.main, 0.1)
                    : lighten(theme.palette.primary.main, 0.95),
                ":hover": {
                  bgcolor: notification.read
                    ? "action.hover"
                    : theme.palette.mode === "dark"
                      ? alpha(theme.palette.primary.main, 0.25)
                      : lighten(theme.palette.primary.main, 0.85),
                },
              }}
              onClick={() => notificationClick(notification)}
            >
              <Tooltip title={notification.created_by}>
                <Avatar
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    fontWeight: 600,
                    fontSize: 20,
                    bgcolor: "primary.main",
                  }}
                >
                  {notification.created_by
                    ? notification.created_by
                        .split(" ")
                        .filter(Boolean)
                        .map((word) => word[0].toUpperCase())
                        .join("")
                        .slice(0, 2)
                    : "N/A"}
                </Avatar>
              </Tooltip>
              <Box
                sx={{
                  display: "flex",
                  flex: 1,
                  flexDirection: "column",
                  gap: 1,
                  overflowX: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flex: 1,
                    mb: 0.5,
                  }}
                >
                  <Typography
                    noWrap
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {notification.title}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 80,
                      justifyContent: "flex-end",
                    }}
                  >
                    <Typography variant="caption">
                      {getTimeAgo(
                        notification.active_from
                          ? notification.active_from
                          : notification.created_at,
                      )}
                    </Typography>
                    {!notification.read && (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: "primary.main",
                        }}
                        title="Unread"
                      />
                    )}
                  </Box>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  {notification.title_description}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
        {isLoading && notifications.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    </Menu>
  );
};
